import { ServerSentEvent } from './sse';

/** What the chat renders out of a streamed Responses API answer. */
export type ChatEvent =
    | { type: 'text'; text: string }
    | { type: 'completed'; responseId: string }
    | { type: 'error'; message: string };

export interface ResponsesRequest {
    input: string;
    stream: true;
    previous_response_id?: string;
}

/** The body of `POST /responses`. The previous response id continues that conversation. */
export function buildResponsesRequest(
    input: string,
    previousResponseId: string | undefined
): ResponsesRequest {
    return previousResponseId === undefined
        ? { input, stream: true }
        : { input, stream: true, previous_response_id: previousResponseId };
}

interface StreamedEventData {
    type?: string;
    delta?: string;
    message?: string;
    response?: { id?: string; error?: { message?: string } };
    error?: { message?: string };
}

/**
 * Maps one server-sent event of a Responses API stream to what the chat renders, or null for
 * events it does not render. The event type comes from the `event` field, or from `type` in the
 * data when the field is absent.
 */
export function toChatEvent(event: ServerSentEvent): ChatEvent | null {
    let data: StreamedEventData;
    try {
        data = JSON.parse(event.data) as StreamedEventData;
    } catch {
        return { type: 'error', message: `Unreadable ${event.event} event from the agent` };
    }
    const type = event.event === 'message' ? (data.type ?? 'message') : event.event;

    switch (type) {
        case 'response.output_text.delta':
            return { type: 'text', text: data.delta ?? '' };
        case 'response.completed':
        case 'response.incomplete':
            return { type: 'completed', responseId: data.response?.id ?? '' };
        case 'response.failed':
            return {
                type: 'error',
                message: data.response?.error?.message ?? 'The agent failed to answer'
            };
        case 'error':
            return {
                type: 'error',
                message: data.message ?? data.error?.message ?? 'The agent reported an error'
            };
        default:
            return null;
    }
}
